import { type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { useViewport } from '@/hooks';
import { DialogBody, DialogHeader, ResponsiveDialog, TooltipProvider } from '@/ui_v3';
import { useClassroomLinking } from '../hooks';
import { type ContentShareType } from '../types';
import { trackClassroomEvent } from '../utils';
import { ClassroomLinkCard } from './ClassroomLinkCard';
import { CreateClassroomButton } from './CreateClassroomButton';
interface ClassroomLinkModalProps {
  contentId: string;
  contentType: ContentShareType;
  onClose: () => void;
  /**
   * 이 콘텐츠가 클래스에 하나라도 연결되면 호출된다.
   * 연결 자체가 목적이 아니라 "연결한 다음 할 일"이 있는 흐름(예: 플레이 젤리 주기)에서
   * 모달을 붙잡아 두지 않고 바로 다음 단계로 넘기려고 쓴다.
   */
  onLinked?: () => void;
}
export function ClassroomLinkModal({
  contentId,
  contentType,
  onClose,
  onLinked
}: ClassroomLinkModalProps) {
  const {
    isMobile
  } = useViewport();
  const {
    t
  } = useTranslation(['common']);
  const {
    classrooms,
    isLoadingClassrooms,
    isPending,
    linkClassroom,
    unlinkClassroom
  } = useClassroomLinking({
    contentId,
    contentType
  });

  // 연결 성공은 목록 상태로 판단한다. linkClassroom 은 mutate 라 반환값이 없고,
  // 성공 시 목록 쿼리가 갱신되면서 isLinked 가 켜진다.
  //
  // 단 **모달이 열린 뒤 새로 연결된 경우에만** 알린다. 열었을 때 이미 연결돼 있었다면
  // 사용자가 한 일이 없는데 모달이 저절로 닫혀 깜빡이는 것처럼 보인다.
  const hasLinkedClassroom = classrooms.some(classroom => classroom.isLinked);
  const wasLinkedOnOpen = useRef<boolean | null>(null);
  useEffect(() => {
    if (isLoadingClassrooms) return;
    if (wasLinkedOnOpen.current === null) {
      wasLinkedOnOpen.current = hasLinkedClassroom;
      return;
    }
    if (hasLinkedClassroom && !wasLinkedOnOpen.current) onLinked?.();
  }, [hasLinkedClassroom, isLoadingClassrooms, onLinked]);
  let classroomListContent: ReactNode;
  if (isLoadingClassrooms) {
    classroomListContent = null;
  } else if (classrooms.length === 0) {
    classroomListContent = <p className="py-lg text-center text-body-sm-medium text-alternative">
        {t('common:classroomLink.emptyMessage')}
      </p>;
  } else {
    classroomListContent = classrooms.map(classroom => <ClassroomLinkCard key={classroom.classGroupId} classroom={classroom} isPending={isPending} isLinked={classroom.isLinked ?? false} linkedClassLinkId={classroom.linkedClassLinkId} onLink={linkClassroom} onUnlink={unlinkClassroom} />);
  }

  // 만들기 버튼 + 클래스 그리드.
  const picker = <TooltipProvider delayDuration={0}>
      <div className="flex h-full min-h-0 flex-col">
        <CreateClassroomButton className="shrink-0" onBeforeOpenClassroom={() => trackClassroomEvent('click_create_classroom', {
        source: 'classroom_link_modal',
        contentId,
        contentType
      })} />
        <div className="mt-sm flex min-h-0 flex-1 flex-wrap content-start gap-[4px] overflow-y-auto">
          {classroomListContent}
        </div>
      </div>
    </TooltipProvider>;
  return <ResponsiveDialog size={isMobile ? 'lg' : 'md'} onClose={onClose} className="mobile-lg:!h-[480px]" data-sentry-element="ResponsiveDialog" data-sentry-component="ClassroomLinkModal" data-sentry-source-file="ClassroomLinkModal.tsx">
      <DialogHeader title={t('common:classroomLink.modalTitle')} hasCloseButton={true} onClose={onClose} data-sentry-element="DialogHeader" data-sentry-source-file="ClassroomLinkModal.tsx" />
      <DialogBody className="flex min-h-0 flex-1 flex-col overflow-hidden px-md pb-lg mobile-lg:px-lg" data-sentry-element="DialogBody" data-sentry-source-file="ClassroomLinkModal.tsx">
        {picker}
      </DialogBody>
    </ResponsiveDialog>;
}
interface ShowClassroomLinkModalPayload {
  contentId: string;
  contentType: ContentShareType;
}
export function showClassroomLinkModal(payload: ShowClassroomLinkModalPayload) {
  trackClassroomEvent('view_classroom_link_modal', {
    source: 'classroom_link_modal',
    contentId: payload.contentId,
    contentType: payload.contentType
  });
  overlay.open(({
    unmount
  }) => <ClassroomLinkModal {...payload} onClose={unmount} />);
}